/**
 * PageNav - 左侧页码导航栏
 */
import { motion } from 'framer-motion';
import { BookOpen, FileText, Check } from 'lucide-react';
import { EditorPage } from '../../../store/useDIYEditorStore';

interface PageNavProps {
  pages: EditorPage[];
  coverFilled: boolean;
  currentPageIndex: number;
  onSelectPage: (index: number) => void;
}

export default function PageNav({ pages, coverFilled, currentPageIndex, onSelectPage }: PageNavProps) {
  return (
    <div className="space-y-1">
      {/* 封面 */}
      <motion.button
        onClick={() => onSelectPage(-1)}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        className={`
          w-full flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-left transition-all
          ${currentPageIndex === -1
            ? 'bg-indigo-600 text-white shadow-md'
            : 'bg-white border border-slate-200 text-slate-700 hover:border-indigo-300 hover:bg-indigo-50/50'
          }
        `}
      >
        <div className={`
          w-7 h-7 rounded-lg flex items-center justify-center shrink-0
          ${currentPageIndex === -1 ? 'bg-white/20' : 'bg-indigo-100'}
        `}>
          <BookOpen className={`w-4 h-4 ${currentPageIndex === -1 ? 'text-white' : 'text-indigo-500'}`} />
        </div>
        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium">封面</span>
        </div>
        {coverFilled && (
          <div className={`
            w-5 h-5 rounded-full flex items-center justify-center shrink-0
            ${currentPageIndex === -1 ? 'bg-white/20' : 'bg-emerald-100'}
          `}>
            <Check className={`w-3 h-3 ${currentPageIndex === -1 ? 'text-white' : 'text-emerald-500'}`} />
          </div>
        )}
      </motion.button>

      {/* 分隔线 */}
      <div className="border-t border-slate-100 my-2" />

      {/* 页面列表 */}
      {pages.map((page, idx) => {
        const isFilled = page.content.trim() && page.illustrationPrompt.trim();
        const isActive = currentPageIndex === idx;

        return (
          <motion.button
            key={page.pageNumber}
            onClick={() => onSelectPage(idx)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className={`
              w-full flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-left transition-all
              ${isActive
                ? 'bg-indigo-600 text-white shadow-md'
                : 'bg-white border border-slate-200 text-slate-700 hover:border-indigo-300 hover:bg-indigo-50/50'
              }
            `}
          >
            <div className={`
              w-7 h-7 rounded-lg flex items-center justify-center shrink-0 text-xs font-bold
              ${isActive ? 'bg-white/20' : 'bg-slate-100 text-slate-500'}
            `}>
              {isFilled && !isActive ? (
                <Check className="w-3.5 h-3.5 text-emerald-500" />
              ) : (
                <FileText className={`w-3.5 h-3.5 ${isActive ? 'text-white' : 'text-slate-400'}`} />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <span className="text-sm font-medium">第 {page.pageNumber} 页</span>
            </div>
          </motion.button>
        );
      })}
    </div>
  );
}
